import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import {
  OCR_CREDIT_ADD_COMPLETE_TITLE,
  OCR_CREDIT_ADD_COMPLETE_MESSAGE,
  OCR_CREDIT_ADD_COMPLETE_BUTTON,
} from '../../../../constants/labels';
import MypageCompleteCard from '../../../uiParts/card/MypageCompleteCard';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import SideBar from '../../../uiParts/menu/SideBar';
import MetaTitle from '../../../utils/MetaTitle';
import { useAdminPageAccessControl } from '../../../../hooks/useAdminPageAccessControl';
import { AdminNoPermissionPage } from '../../../uiParts/parts/AdminNoPermission';
import { usePageLayout } from '../../../../hooks/usePageLayout';

interface LocationState {
  fromOcrCreditAdd?: boolean;
}

const OcrCreditAddComplete: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { noPermission, isChecking } = useAdminPageAccessControl('OcrCreditAddComplete');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  const state = location.state as LocationState | null;
  const isValidAccess = Boolean(state?.fromOcrCreditAdd);

  useEffect(() => {
    const handlePopState = (): void => {
      setSnackbarOpen(true);
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (isChecking || noPermission) return;

    if (!isValidAccess) {
      navigate(`/admin/company/${companyId}`);
    }
  }, [isChecking, noPermission, isValidAccess, navigate, companyId]);

  if (isChecking) {
    return (
      <>
        <MetaTitle customTitle={OCR_CREDIT_ADD_COMPLETE_TITLE} />
        <CustomLoading loading={true} />
      </>
    );
  }

  if (noPermission) {
    return (
      <AdminNoPermissionPage companyId={companyId} customTitle={OCR_CREDIT_ADD_COMPLETE_TITLE} />
    );
  }

  if (!isValidAccess) {
    return null;
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle={OCR_CREDIT_ADD_COMPLETE_TITLE} />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          layoutStyles.pageContentNoFooter,
          layoutStyles.fullHeightContent,
          {
            justifyContent: 'center',
            alignItems: 'center',
          },
        ]}
      >
        <MypageCompleteCard
          IconComponent={CheckCircleIcon}
          titleText={OCR_CREDIT_ADD_COMPLETE_MESSAGE}
          titleStyle={{ fontWeight: '500' }}
          isMessageCenter
          messageText=""
          buttonText={OCR_CREDIT_ADD_COMPLETE_BUTTON}
          targetPath={`/admin/company/${companyId}`}
        />
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text=""
        infoText="前のページに戻れません。"
      />
    </Box>
  );
};

export default OcrCreditAddComplete;
